import type { ComparisonPage } from '../comparisons';

const CHECKED = '2026-10-06';
/** "6 October 2026": the prose date comes from the same constant as the sources and lastReviewed. */
const CHECKED_LONG = new Date(`${CHECKED}T12:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });

/** /compare/jobber-vs-housecall-pro. Draft 2026-10-06. Neutral them-vs-them page; re-verify every row on publish day. */
const page: ComparisonPage = {
  slug: 'compare-jobber-vs-housecall-pro',
  path: '/compare/jobber-vs-housecall-pro',
  primaryKeyword: 'jobber vs housecall pro',
  secondaryKeywords: ['housecall pro vs jobber', 'jobber vs housecall pro pricing', 'jobber or housecall pro for small business'],
  title: 'Jobber vs Housecall Pro (2026): Sourced Comparison | Scopie', // 58
  metaDescription:
    "Jobber vs Housecall Pro, compared from each company's pricing page: plans, users, trial, quoting, routing and QuickBooks. Every fact sourced and dated.",
  navLabel: 'Jobber vs Housecall Pro',
  h1: 'Jobber vs Housecall Pro: a sourced comparison',
  answerFirst:
    'Jobber and Housecall Pro are both field service apps for home service businesses, with quoting, scheduling, invoicing and online payments. On their own pricing pages, Jobber starts lower for a single user and Housecall Pro includes more users in its middle and top plans. Both offer a free trial without a credit card.',
  lastReviewed: CHECKED,
  sections: [
    {
      id: 'verdict',
      h2: 'The short version',
      body: [
        'Choose Jobber if you are a solo operator who wants the lowest entry price, or if you want to grow into features such as QuickBooks Online sync and automated follow-ups on a mid-tier plan. Jobber prices each plan by team size: 1, 5, 10 or 15 users.',
        'Choose Housecall Pro if your team is around 5 to 8 people and you want route planning and recurring service plans. Its Essentials plan includes 5 users and its Max plan includes 8 users, with route optimization and recurring service plans listed on Max.',
        'Both companies change prices and plan contents. Every fact below links to the page it came from and the date it was checked.',
      ],
    },
    {
      id: 'pricing',
      h2: 'Pricing and users',
      body: [
        'Jobber, from its pricing page: for one user, Core is 49 dollars a month (29 billed annually), Connect 139 (99 annually) and Grow 199 (149 annually). For 5 users, Connect is 199 dollars a month (149 annually) and Grow 299 (229 annually). For 10 users, Connect is 299 (229 annually) and Grow 399 (299 annually). Jobber says annual billing saves up to 25 percent.',
        'Housecall Pro, from its pricing page: Basic includes 1 user at 79 dollars a month (59 billed annually); Essentials includes 5 users at 189 dollars a month (149 annually); Max includes 8 users at 329 dollars a month (299 annually), with additional users at 35 dollars a month each. At the time of checking, the page also showed limited-time discounts.',
        'For one person, Jobber Core is the lower entry price. For a team of 5, Jobber Connect and Housecall Pro Essentials list the same annual price. For a team of 8, Housecall Pro Max includes all 8 users, while Jobber moves to its 10-user tier.',
      ],
    },
    {
      id: 'quoting',
      h2: 'Quoting and estimates',
      body: [
        'Both send professional quotes or estimates. Housecall Pro says customers can sign estimates from desktop or mobile, and it lists a visual sales proposal tool that is included on Max and costs extra on Basic and Essentials. Jobber lists optional line items and quotes with rich visuals and reviews on its Grow plan, and automated quote follow-ups on Connect and up.',
      ],
    },
    {
      id: 'routing',
      h2: 'Scheduling and routing',
      body: [
        'Both include job scheduling. Housecall Pro lists "Routes" to group and sequence jobs on Essentials, and route optimization based on drive time and traffic on Max, and it has a dedicated route optimization page. Jobber\'s pricing page, checked on the same day, did not list routing among the plan features, so it is not compared here; check Jobber\'s features page if routing matters to you.',
      ],
    },
    {
      id: 'accounting',
      h2: 'Payments and accounting',
      body: [
        'Both take online payments. Jobber lists QuickBooks Online sync on Connect and Grow. Housecall Pro lists QuickBooks Online sync on its pricing page and has a QuickBooks integration page, and it also lists bank payments, consumer financing and mobile check deposit.',
      ],
    },
    {
      id: 'third-option',
      h2: 'A third option for quote-first businesses: Scopie',
      body: [
        'If most of your work starts with a quote and you mainly need line-item quotes signed online, scheduling with crew and equipment, and invoices, Scopie is a newer, simpler option, made by the publisher of this page. It does less than either tool: no QuickBooks sync, no SMS and no route optimization.',
      ],
    },
  ],
  table: {
    caption: `Jobber and Housecall Pro, from each company's pricing page, checked ${CHECKED}.`,
    columns: ['', 'Jobber', 'Housecall Pro'],
    rows: [
      { label: 'Plans', cells: [{ text: '' }, { text: 'Core, Connect, Grow', sources: ['jobber-pricing'] }, { text: 'Basic, Essentials, Max', sources: ['hcp-pricing'] }] },
      { label: 'Users included', cells: [{ text: '' }, { text: 'Core 1 user; Connect and Grow priced for 1, 5, 10 or 15 users', sources: ['jobber-pricing'] }, { text: 'Basic 1, Essentials 5, Max 8 (extra users on Max at a monthly fee)', sources: ['hcp-pricing'] }] },
      { label: 'Free trial', cells: [{ text: '' }, { text: 'Yes, no credit card required', sources: ['jobber-pricing'] }, { text: '14 days, no credit card required', sources: ['hcp-pricing'] }] },
      { label: 'E-signature on quotes', cells: [{ text: '' }, { text: 'Not stated on the pricing page' }, { text: 'Yes, from desktop or mobile', sources: ['hcp-estimating'] }] },
      { label: 'Optional line items', cells: [{ text: '' }, { text: 'Grow', sources: ['jobber-pricing'] }, { text: 'Not compared' }] },
      { label: 'Automated quote and invoice follow-ups', cells: [{ text: '' }, { text: 'Connect and Grow', sources: ['jobber-pricing'] }, { text: 'Not compared' }] },
      { label: 'Route planning', cells: [{ text: '' }, { text: 'Not listed on the pricing page' }, { text: 'Routes on Essentials; route optimization on Max', sources: ['hcp-pricing', 'hcp-routing'] }] },
      { label: 'Recurring service plans', cells: [{ text: '' }, { text: 'Not listed on the pricing page' }, { text: 'Max', sources: ['hcp-pricing', 'hcp-service-plans'] }] },
      { label: 'QuickBooks Online sync', cells: [{ text: '' }, { text: 'Connect and Grow', sources: ['jobber-pricing'] }, { text: 'Yes (plan not confirmed)', sources: ['hcp-pricing', 'hcp-quickbooks'] }] },
      { label: 'Two-way SMS', cells: [{ text: '' }, { text: 'Grow', sources: ['jobber-pricing'] }, { text: 'Not compared' }] },
    ],
  },
  faq: [
    {
      q: 'Which is cheaper for one person, Jobber or Housecall Pro?',
      a: 'On the pricing pages checked on ' + CHECKED_LONG + ', Jobber Core had the lower entry price for one user, both monthly and billed annually. Check both pricing pages for current prices.',
    },
    {
      q: 'Which is cheaper for a team of 5?',
      a: 'On the same date, Jobber Connect for 5 users and Housecall Pro Essentials, which includes 5 users, listed the same annual price per month. Jobber Grow for 5 users cost more. Compare the features in each plan, not just the price.',
    },
    {
      q: 'Does Housecall Pro have route optimization?',
      a: 'Yes. Housecall Pro lists route optimization on its Max plan and has a route optimization page, and its Essentials plan lists Routes for grouping and sequencing jobs.',
    },
    {
      q: 'Do Jobber and Housecall Pro sync with QuickBooks?',
      a: 'Both list QuickBooks Online sync. Jobber includes it on Connect and Grow. Housecall Pro lists it on its pricing page and has a QuickBooks integration page.',
    },
    {
      q: 'Do both have free trials?',
      a: 'Yes. Both offer a free trial without a credit card. Housecall Pro states 14 days.',
    },
  ],
  sources: [
    { id: 'jobber-pricing', url: 'https://www.getjobber.com/pricing/', checked: CHECKED, how: 'rendered page in a headless browser; team-size selector read for 1, 2-5, 6-10 and 11-15 people' },
    { id: 'hcp-pricing', url: 'https://www.housecallpro.com/pricing/', checked: CHECKED, how: 'fetched HTML' },
    { id: 'hcp-estimating', url: 'https://www.housecallpro.com/features/estimating-software/', checked: CHECKED, how: 'fetched HTML' },
    { id: 'hcp-routing', url: 'https://www.housecallpro.com/features/route-optimization-software/', checked: CHECKED, how: 'fetched HTML' },
    { id: 'hcp-service-plans', url: 'https://www.housecallpro.com/features/recurring-service-plans/', checked: CHECKED, how: 'fetched HTML' },
    { id: 'hcp-quickbooks', url: 'https://www.housecallpro.com/features/quickbooks/', checked: CHECKED, how: 'fetched HTML' },
  ],
  disclaimer:
    'Jobber and Housecall Pro are trademarks of their respective owners. Scopie is not affiliated with either company. Facts were taken from each company\'s public pages on the date shown and may have changed since. This page is published by Scopie, which makes competing software.',
  internalLinks: [
    { href: '/alternatives/jobber', anchor: 'Scopie as a Jobber alternative', status: 'live' },
    { href: '/pricing', anchor: 'Scopie plans and pricing', status: 'live' },
    { href: '/for', anchor: 'Industries Scopie is built for', status: 'live' },
  ],
  cta: {
    heading: 'Mostly quoting? Try Scopie',
    text: 'Line-item quotes signed online, then jobs and invoices. Free for 14 days.',
    button: 'Start free',
    href: '/register',
    fine: '14-day free trial. No credit card required.',
  },
  claimsToVerify: [
    'Re-open all six sources on publish day; update every price, plan and feature row, and lastReviewed.',
    'Pricing figures are in words to pass the site\'s "no $ outside example quotes" test; web-f1 may add an exception for comparison pages.',
    'Earlier conflict resolved: Jobber\'s own comparison snippet said Housecall Pro relies on third parties for routing, but Housecall Pro\'s pricing page lists Routes (Essentials) and route optimization (Max). This page follows Housecall Pro\'s own page.',
    'Jobber routing and recurring rows say "Not listed on the pricing page" (true on 2026-10-06; Jobber\'s features pages are behind a bot check and were not read).',
    'Housecall Pro QuickBooks plan level not confirmed (listed in the pricing feature list; plan placement ambiguous in the fetched text).',
    'Owner/legal review before publishing.',
  ],
};

export default page;
